// Shared admin actions for one license row: revoke + unbind, each behind a
// ConfirmDialog with an optional audit reason. Used by /admin/licenses and
// /admin/users/:id — single implementation (no per-page duplicates).

import { useState } from "react";
import { fieldErrorClass } from "@ahcut/ui/recipes";
import { Button, Field } from "@/ui";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { revokeAdminLicense, unbindAdminLicense } from "@/lib/admin";
import type { AdminLicense } from "@/lib/types";

type PendingAction = "revoke" | "unbind" | null;

export function LicenseAdminActions({
  license,
  onDone,
}: {
  license: AdminLicense;
  /** Called after a successful action so the parent list refetches. */
  onDone: () => void;
}) {
  const [pending, setPending] = useState<PendingAction>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const revoked = license.revoked_at !== null;
  const unbound = license.machine_id === null;

  function open(action: Exclude<PendingAction, null>) {
    setReason("");
    setError(null);
    setPending(action);
  }

  async function run() {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      if (pending === "revoke") {
        await revokeAdminLicense(license.id, reason.trim());
      } else {
        await unbindAdminLicense(license.id, reason.trim());
      }
      setPending(null);
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        variant="quiet"
        size="sm"
        onClick={() => open("unbind")}
        disabled={revoked || unbound}
        title={
          revoked
            ? "Lisensi sudah dicabut"
            : unbound
              ? "Belum terikat perangkat"
              : "Lepas ikatan perangkat"
        }
      >
        Lepas ikatan
      </Button>
      <Button
        variant="danger"
        size="sm"
        onClick={() => open("revoke")}
        disabled={revoked}
        title={revoked ? "Lisensi sudah dicabut" : "Cabut lisensi"}
      >
        Cabut
      </Button>

      <ConfirmDialog
        open={pending === "unbind"}
        title="Lepas ikatan perangkat?"
        confirmLabel="Lepas ikatan"
        busy={busy}
        onConfirm={run}
        onCancel={() => setPending(null)}
      >
        <p>
          Ikatan perangkat lisensi ini dihapus. Pengguna dapat langsung mengaktifkan ke
          perangkat baru tanpa menunggu cooldown transfer.
        </p>
        <Field
          label="Alasan (opsional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="mis. laptop rusak"
        />
        {error && <p className={fieldErrorClass}>{error}</p>}
      </ConfirmDialog>

      <ConfirmDialog
        open={pending === "revoke"}
        title="Cabut lisensi?"
        confirmLabel="Cabut lisensi"
        danger
        busy={busy}
        onConfirm={run}
        onCancel={() => setPending(null)}
      >
        <p>
          Lisensi ditandai dicabut. Catatan: verifikasi desktop berjalan offline — pencabutan
          hanya tercermin di pemeriksaan status online, bukan kill switch.
        </p>
        <Field
          label="Alasan (opsional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="mis. refund manual"
        />
        {error && <p className={fieldErrorClass}>{error}</p>}
      </ConfirmDialog>
    </div>
  );
}
